import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Home, FileText, TrendingUp, UtensilsCrossed, User, Users, Search } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const Layout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  const patientNavItems = [
    { path: '/', icon: Home, label: 'Dashboard' },
    { path: '/receitas', icon: FileText, label: 'Receitas' },
    { path: '/progresso', icon: TrendingUp, label: 'Progresso' },
    { path: '/plano-alimentar', icon: UtensilsCrossed, label: 'Plano' },
    { path: '/perfil', icon: User, label: 'Perfil' },
  ];

  const doctorNavItems = [
    { path: '/medico/dashboard', icon: Users, label: 'Pacientes' },
    { path: '/perfil', icon: User, label: 'Perfil' },
  ];

  const adminNavItems = [
    { path: '/admin/dashboard', icon: Users, label: 'Gerenciar' },
    { path: '/perfil', icon: User, label: 'Perfil' },
  ];

  const navItems = user?.role === 'admin' ? adminNavItems : 
                   user?.role === 'doctor' ? doctorNavItems : patientNavItems;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#f0f2f5] via-[#f5f7fa] to-[#eef1f5] pb-24 md:pb-0">
      
      {/* Floating Header - Fixed on mobile, below notch */}
      <header className="fixed md:sticky top-0 left-0 right-0 z-[100] px-[4%] md:px-[5%] pt-2 md:pt-4 pb-1 md:pb-0" style={{ background: 'linear-gradient(to bottom, #f0f2f5 0%, #f0f2f5 80%, transparent 100%)', paddingTop: 'max(env(safe-area-inset-top, 0px), 8px)' }}>
        <div className="md:ml-64 lg:ml-72 md:bg-transparent">
          <div className="floating-header px-4 sm:px-6 py-2 sm:py-4 flex items-center justify-between">
            {/* Logo - visible on mobile (20% bigger: 8px -> 10px = 25% increase) */}
            <div className="md:hidden">
              <img 
                src="/logo-wp.webp" 
                alt="Logo" 
                className="h-10 w-auto object-contain"
              />
            </div>
            
            {/* Search Bar - visible on desktop */}
            <div className="hidden md:flex items-center gap-3 flex-1 max-w-lg">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
                <input 
                  type="text"
                  placeholder="Buscar..."
                  className="w-full pl-11 pr-4 py-2.5 bg-muted/50 border-0 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#0F2B4C]/30 transition-all"
                />
              </div>
            </div>
            
            {/* User Info */}
            {user && (
              <div className="flex items-center gap-3 sm:gap-4">
                <div className="text-right hidden sm:block">
                  <p className="text-xs text-muted-foreground">Bem-vindo(a)</p>
                  <p className="text-sm font-semibold text-foreground">{user.nome?.split(' ').slice(0, 2).join(' ')}</p>
                </div>
                <span className="text-xs sm:text-sm text-muted-foreground sm:hidden">
                  {user.nome?.split(' ')[0]}
                </span>
                {user.foto_perfil ? (
                  <div className="relative">
                    <img 
                      src={user.foto_perfil} 
                      alt={user.nome}
                      className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl object-cover shadow-lg border-2 border-white"
                    />
                    <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 bg-green-500 rounded-full border-2 border-white"></div>
                  </div>
                ) : (
                  <div className="relative">
                    <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-gradient-to-br from-[#B8977A] to-[#9C7D5E] flex items-center justify-center text-white font-bold text-base sm:text-lg shadow-lg border-2 border-white">
                      {user.nome?.charAt(0)?.toUpperCase() || 'U'}
                    </div>
                    <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 bg-green-500 rounded-full border-2 border-white"></div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Main Content - with top padding for fixed header on mobile */}
      <main className="px-[4%] md:px-[5%] pt-[72px] md:pt-6 pb-6 md:ml-64 lg:ml-72">
        <div className="w-full">
          {children}
        </div>
      </main>

      {/* Floating Bottom Navigation for Mobile */}
      <nav className="fixed bottom-4 left-[4%] right-[4%] md:hidden z-50">
        <div className="floating-card px-2 py-2">
          <div className="flex justify-around items-center">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <button
                  key={item.path}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                  onClick={() => navigate(item.path)}
                  className={`flex flex-col items-center justify-center px-4 py-2 rounded-xl transition-all ${
                    isActive 
                      ? 'bg-gradient-to-br from-[#0F2B4C] to-[#1C3D66] text-white shadow-lg shadow-[#0F2B4C]/30' 
                      : 'text-muted-foreground hover:bg-muted/50'
                  }`}
                >
                  <Icon size={20} />
                  <span className="text-[10px] mt-1 font-medium">{item.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </nav>

      {/* Floating Sidebar for Desktop */}
      <aside className="hidden md:block fixed left-[2%] lg:left-[3%] top-5 bottom-5 w-56 lg:w-60 z-[200]">
        <div className="floating-sidebar h-full px-4 pt-4 pb-5 flex flex-col">
          {/* Logo */}
          <div className="mb-5 flex-shrink-0">
            <div className="bg-white rounded-xl p-3 flex items-center justify-center shadow-md border border-[#B8977A]/30">
              <img 
                src="/logo-wp.webp" 
                alt="Logo" 
                className="h-12 w-full object-contain"
              />
            </div>
          </div>
          
          {/* Navigation */}
          <nav className="flex-1 space-y-2">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <button
                  key={item.path}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                  onClick={() => navigate(item.path)}
                  className={`w-full flex items-center gap-3 px-4 py-3.5 rounded-xl font-medium transition-all ${
                    isActive
                      ? 'bg-gradient-to-r from-[#0F2B4C] to-[#1C3D66] text-white shadow-lg shadow-[#0F2B4C]/30'
                      : 'text-muted-foreground hover:bg-[#0F2B4C]/10 hover:text-[#0F2B4C]'
                  }`}
                >
                  <div className={`p-1.5 rounded-lg ${isActive ? 'bg-white/20' : ''}`}>
                    <Icon size={18} />
                  </div>
                  <span>{item.label}</span>
                </button>
              );
            })}
          </nav>
          
          {/* User Mini Card at Bottom */}
          <div className="mt-auto pt-4 border-t border-border/50">
            <div className="flex items-center gap-3 px-2 py-2">
              {user?.foto_perfil ? (
                <img 
                  src={user.foto_perfil} 
                  alt={user?.nome}
                  className="w-9 h-9 rounded-xl object-cover"
                />
              ) : (
                <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#B8977A] to-[#9C7D5E] flex items-center justify-center text-white font-bold text-xs">
                  {user?.nome?.charAt(0)?.toUpperCase() || 'U'}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground truncate">{user?.nome?.split(' ')[0]}</p>
                <p className="text-xs text-muted-foreground capitalize">{user?.role === 'admin' ? 'Administrador' : user?.role === 'doctor' ? 'Médico' : 'Paciente'}</p>
              </div>
            </div>
          </div>
        </div>
      </aside>
    </div>
  );
};

export default Layout;
